import '../../styles/catalog.css';
import { useMemo, useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { SiteLink as Link } from '../../components/SiteLink';
import { PageShell, GetStarted } from '../../components';
import { PageBody, PageHero } from '../../components/page';
import { CATALOG, CATEGORY_LABELS, type CatalogCategory } from '../../catalog';
import { ServerCard } from './ServerCard';

const CATEGORIES = (Object.keys(CATEGORY_LABELS) as CatalogCategory[])
  .map((c) => ({ id: c, count: CATALOG.filter((s) => s.category === c).length }))
  .filter((c) => c.count > 0);

const matches = (q: string) => {
  const needle = q.trim().toLowerCase();
  if (!needle) return () => true;
  return (s: (typeof CATALOG)[number]) =>
    [s.name, s.description, s.publisher ?? '', ...(s.tags ?? [])].some((f) => f.toLowerCase().includes(needle));
};

/**
 * statio.dev/mcp-servers. The app's catalog view (Pugio PreBuilts.tsx), public:
 * search, category filters and a card per server, from src/catalog. The whole
 * list is in the prerendered HTML, so every server is a crawlable link before
 * any JavaScript runs; filtering only hides cards.
 */
export default function CatalogIndex() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<CatalogCategory | 'all'>('all');
  const shown = useMemo(
    () => CATALOG.filter(matches(query)).filter((s) => category === 'all' || s.category === category),
    [query, category],
  );

  return (
    <PageShell>
      <PageBody>
        <div className="ct-hero">
          <PageHero
            title="MCP server catalog"
            lede={`${CATALOG.length} MCP servers you can connect through Statio, with access granted one tool at a time.`}
          >
            <p className="ct-new">
              New to MCP?{' '}
              <Link className="s-link" to="/tutorials/what-is-mcp">
                What is an MCP server?
              </Link>
            </p>
          </PageHero>
        </div>

        <div className="ct-controls">
          <label className="ct-search">
            <MagnifyingGlass size={16} aria-hidden="true" />
            <span className="sr-only">Search the catalog</span>
            <input
              type="search"
              placeholder="Search by name, vendor or what it does"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <div className="ct-chips" role="group" aria-label="Category">
            <button
              type="button"
              className="ct-chip"
              aria-pressed={category === 'all'}
              onClick={() => setCategory('all')}
            >
              All <span>{CATALOG.length}</span>
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className="ct-chip"
                aria-pressed={category === c.id}
                onClick={() => setCategory(c.id)}
              >
                {CATEGORY_LABELS[c.id]} <span>{c.count}</span>
              </button>
            ))}
          </div>
        </div>

        <p className="ct-count" aria-live="polite">
          {shown.length === CATALOG.length
            ? `${CATALOG.length} servers`
            : `${shown.length} of ${CATALOG.length} servers`}
        </p>

        {shown.length > 0 ? (
          <ul className="ct-grid">
            {shown.map((s) => (
              <ServerCard key={s.slug} server={s} />
            ))}
          </ul>
        ) : (
          <p className="ct-empty">
            Nothing matches that yet. If there is a server you need,{' '}
            <a className="s-link" href="mailto:dans@romans.dev?subject=MCP%20server%20request">
              tell us which one
            </a>
            .
          </p>
        )}
      </PageBody>
      <GetStarted />
    </PageShell>
  );
}
